import type { LiveChapter, PrepSegment } from '../types/liveCall';

/**
 * The lesson's chapters: since decision 128, the coach's saved moments, each
 * a segment as the async editor makes them (15 s before the playhead, its
 * handles, a title and a note), kept on the booking as liveChapters
 * (docs/booking-contracts.md section 8, at most 50, the coach's to write).
 * The whole video stays: the bar runs from 0:00 to the end, the segments in
 * it and the stretches between them as unnamed parts; the chapters panel
 * lists only the segments. Flags from before 128 are points; each runs to
 * the next, as it did. Pure, so tested.
 */

export const MAX_CHAPTERS = 50;
/** A stretch between segments shorter than this goes to the segment after it. */
const MIN_PART_S = 0.5;

/** A part of the bar: a saved moment, or (gap) the video between them, not listed. */
export interface ChapterMark { label: string; start: number; end: number; gap?: boolean; /** The saved moment it draws. */ id?: string }

/** The bar's parts, end to end over the whole video: the segments, and between them the video's own name. */
export function segmentMarks(chapters: LiveChapter[] | null | undefined, duration: number | null | undefined, videoLabel: string): ChapterMark[] {
  if (!chapters?.length || !duration || !(duration > 0)) return [];
  const clamp = (t: number) => Math.max(0, Math.min(duration, t));
  const sorted = [...chapters]
    .map((c) => ({ id: c.id, label: c.label.trim() || 'Saved moment', start: clamp(c.startTime), end: clamp(c.endTime) }))
    .sort((a, b) => a.start - b.start);
  // A point (a flag from before 128) runs to the next one, or to the end.
  const segs = sorted.map((c, i) => ({ ...c, end: c.end > c.start ? c.end : (sorted.find((n, j) => j > i && n.start > c.start)?.start ?? duration) }));
  const marks: ChapterMark[] = [];
  let at = 0;
  for (const sg of segs) {
    let start = Math.max(sg.start, at);
    if (sg.end - start < MIN_PART_S) continue;
    if (start - at < MIN_PART_S) start = at;
    else marks.push({ label: videoLabel, start: at, end: start, gap: true });
    marks.push({ label: sg.label, start, end: sg.end, id: sg.id });
    at = sg.end;
  }
  if (!marks.length) return [];
  if (duration - at >= MIN_PART_S) marks.push({ label: videoLabel, start: at, end: duration, gap: true });
  else marks[marks.length - 1].end = duration;
  return marks;
}

/** A saved moment as the editor's segment modal confirms it. */
export function newSegment(startTime: number, endTime: number, label: string, note?: string, nowMs = Date.now()): LiveChapter {
  const r = (t: number) => Math.max(0, Math.round(t * 1000) / 1000);
  const n = note?.trim();
  return { id: `ch-${nowMs}`, label: label.trim(), startTime: r(startTime), endTime: r(Math.max(startTime, endTime)), ...(n ? { note: n } : {}) };
}

/** The list with one more, in time order; null when it is full (the rules refuse a 51st). */
export function withChapter(list: LiveChapter[] | null | undefined, ch: LiveChapter): LiveChapter[] | null {
  const cur = list ?? [];
  if (cur.length >= MAX_CHAPTERS) return null;
  return [...cur, ch].sort((a, b) => a.startTime - b.startTime);
}

/**
 * A lesson's chapters: liveChapters, or, until the coach writes those, the
 * flags made before them (prepSegments), so none made while preparing is lost.
 */
export function lessonChapters(b: { liveChapters?: LiveChapter[] | null; prepSegments?: PrepSegment[] | null } | null | undefined): LiveChapter[] {
  if (b?.liveChapters?.length) return b.liveChapters;
  return (b?.prepSegments ?? []).map((s) => ({ id: s.id, label: s.label, startTime: s.startTime, endTime: s.endTime, ...(s.notes ? { note: s.notes } : {}) }));
}

/** Decision 137: the one grey line under the player, both roles: "3 saved moments". None, no line. */
export function savedMomentsLine(count: number): string | null {
  if (count < 1) return null;
  return `${count} saved moment${count === 1 ? '' : 's'}`;
}

/**
 * A saved moment's side-by-side as the call opens it (145, 149): the clip, its name and where to start it.
 * "This match" is the match itself at the segment's bounds; any other source needs its own URL. None
 * when nothing is attached or it cannot be played.
 */
export function sideBySideClip(c: LiveChapter, matchUrl: string): { url: string; label: string; start: number } | null {
  const s = c.sideBySide;
  if (!s) return null;
  const url = s.videoUrl || (s.source === 'this-match' ? matchUrl : '');
  if (!url) return null;
  return { url, label: s.label?.trim() || c.label.trim() || 'Side by side', start: Math.max(0, s.startTime ?? 0) };
}
